import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Maximize2, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import EnquiryPopup from '@/components/EnquiryPopup';

const PropertyCard = ({ property, variant = 'default' }) => {
  const [showEnquiry, setShowEnquiry] = useState(false);
  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    if (url.startsWith('http')) {
      try {
        const parsed = new URL(url);
        if (parsed.pathname.startsWith('/api/uploads')) {
          return `${process.env.REACT_APP_BACKEND_URL}${parsed.pathname}`;
        }
      } catch (e) {}
      return url;
    }
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };
  const formatPrice = (price, listingType) => {
    if (!price) return null;
    let formatted;
    if (price >= 10000000) {
      formatted = `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      formatted = `₹${(price / 100000).toFixed(2)} L`;
    } else {
      formatted = `₹${price.toLocaleString()}`;
    }
    return listingType === 'lease' ? `${formatted}/mo` : formatted;
  };

  const formatArea = (area) => {
    return `${area.toLocaleString()} sq.ft.`;
  };

  const getPerSqftPrice = (price, area) => {
    if (!price || !area) return null;
    return Math.round(price / area);
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      office: 'Office Space',
      retail: 'Retail Shop',
      mall_shop: 'Mall Shop',
      sco: 'SCO Plot',
      commercial_plot: 'Commercial Plot',
      coworking: 'Co-working',
      mixed_use: 'Mixed Use',
    };
    return labels[type] || type;
  };

  if (variant === 'featured') {
    return (
      <div
        className="group relative overflow-hidden bg-white hover:shadow-2xl transition-all duration-500 rounded-xl border border-[rgba(201,168,76,0.2)]"
        data-testid={`property-card-${property.id}`}
      >
        <div className="relative h-72 overflow-hidden rounded-t-xl">
          <img
            src={resolveUrl(property.images?.[0]) || 'https://images.unsplash.com/photo-1549851501-a521e1e3fb85?w=800'}
            alt={property.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Listing Type Badge */}
          <div className="absolute top-4 right-4">
            <span className={`px-3 py-1 text-xs uppercase tracking-wider font-bold rounded-md ${
              property.listing_type === 'lease' 
                ? 'bg-[#c9a84c] text-white' 
                : 'bg-[#16A34A] text-white'
            }`}>
              For {property.listing_type === 'lease' ? 'Lease' : 'Sale'}
            </span>
          </div>
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            {property.is_featured && (
              <span className="badge-featured">Featured</span>
            )}
            {property.is_new_launch && (
              <span className="badge-new">New Launch</span>
            )}
            {property.is_hot_deal && (
              <span className="badge-hot">Hot Deal</span>
            )}
          </div>
          
          {/* Price overlay */}
          <div className="absolute bottom-4 left-4 right-4">
            <p className="text-white text-2xl font-bold">
              {property.price_on_request ? 'Price on Request' : formatPrice(property.price, property.listing_type)}
            </p>
            {!property.price_on_request && property.area_sqft && property.price && (
              <p className="text-white/70 text-sm">
                ₹{getPerSqftPrice(property.price, property.area_sqft).toLocaleString()}/sq.ft.
                {property.listing_type === 'lease' && '/mo'}
              </p>
            )}
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex items-center gap-2 text-[#c9a84c] text-xs uppercase tracking-wider mb-2 font-semibold">
            <Building2 className="w-4 h-4" />
            {getPropertyTypeLabel(property.property_type)}
          </div>
          
          <h3 className="text-xl font-bold text-[#2a1e00] mb-3 line-clamp-2 group-hover:text-[#c9a84c] transition-colors">
            {property.title}
          </h3>
          
          <div className="flex items-center gap-4 text-sm text-[#6a5a30] mb-4">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {property.sector}
            </div>
            <div className="flex items-center gap-1">
              <Maximize2 className="w-4 h-4" />
              {formatArea(property.area_sqft)}
            </div>
          </div>
          
          <div className="flex gap-3">
            <Link to={`/property/${property.id}`} className="flex-1">
              <Button className="btn-primary w-full" data-testid={`view-property-${property.id}`}>
                View Details
              </Button>
            </Link>
            <Link to={`/property/${property.id}?enquiry=true`}>
              <Button className="btn-secondary" data-testid={`enquire-property-${property.id}`} onClick={(e) => { e.preventDefault(); setShowEnquiry(true); }}>
                Enquire
              </Button>
            </Link>
          </div>
        </div>
        <EnquiryPopup open={showEnquiry} onClose={() => setShowEnquiry(false)} property={property} />
      </div>
    );
  }

  // Default compact variant
  return (
    <Link
      to={`/property/${property.id}`}
      className="group block bg-white hover:shadow-xl transition-all duration-500 hover-lift rounded-xl border border-[rgba(201,168,76,0.2)]"
      data-testid={`property-card-${property.id}`}
    >
      <div className="relative h-48 overflow-hidden rounded-t-xl">
        <img
          src={property.images?.[0] || 'https://images.unsplash.com/photo-1549851501-a521e1e3fb85?w=800'}
          alt={property.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Listing Type Badge */}
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded ${
            property.listing_type === 'lease' 
              ? 'bg-[#c9a84c] text-white' 
              : 'bg-[#16A34A] text-white'
          }`}>
            {property.listing_type === 'lease' ? 'Lease' : 'Sale'}
          </span>
        </div>
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {property.is_featured && (
            <span className="badge-featured text-[10px]">Featured</span>
          )}
          {property.is_new_launch && (
            <span className="badge-new text-[10px]">New</span>
          )}
          {property.is_hot_deal && (
            <span className="badge-hot text-[10px]">Hot</span>
          )}
        </div>
      </div>
      
      <div className="p-5">
        <div className="flex items-center gap-2 text-[#c9a84c] text-[10px] uppercase tracking-wider mb-2 font-semibold">
          <Building2 className="w-3 h-3" />
          {getPropertyTypeLabel(property.property_type)}
        </div>
        
        <h3 className="text-base font-bold text-[#2a1e00] mb-2 line-clamp-2 group-hover:text-[#c9a84c] transition-colors">
          {property.title}
        </h3>
        
        <div className="flex items-center gap-1 text-sm text-[#6a5a30] mb-3">
          <MapPin className="w-3 h-3" />
          {property.sector}
        </div>
        
        <div className="flex justify-between items-center pt-3 border-t border-[rgba(201,168,76,0.2)]">
          <div>
            <p className="text-lg font-bold text-[#2a1e00]">
              {property.price_on_request ? 'Price on Request' : formatPrice(property.price, property.listing_type)}
            </p>
            {!property.price_on_request && property.area_sqft && property.price && (
              <p className="text-[10px] text-[#c9a84c] font-medium">
                ₹{getPerSqftPrice(property.price, property.area_sqft).toLocaleString()}/sq.ft.
              </p>
            )}
          </div>
          <p className="text-xs text-[#6a5a30]">
            {formatArea(property.area_sqft)}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
